
'use client';

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { TripHistory } from "@/components/trip-history";
import Speedometer from "@/components/speedometer";
import { TrafficLights } from "@/components/traffic-lights";
import { Logo } from "@/components/icons";
import type { Trip, TripDataPoint, SpeedCheckResult } from "@/lib/types";
import { Car, Gauge, History, Loader2, Power, Square, Timer, Waypoints, Zap, Flag, TimerOff, ParkingCircle, AlertTriangle, Pin, RefreshCw, Milestone } from "lucide-react";
import { cn, haversineDistance } from "@/lib/utils";
import { playCountdownBeep, playGoBeep, playMarkerStartSound, playMarkerEndSound, playStationarySound, playStopSound, playLaserBeep, playIntroTheme, play100kmhSound } from "@/lib/sounds";

const formatDuration = (seconds: number) => {
    const totalSeconds = Math.round(seconds);
    if (totalSeconds < 60) {
        return { value: totalSeconds.toString(), unit: 's' };
    }
    if (totalSeconds < 3600) {
        return { value: (totalSeconds / 60).toFixed(1), unit: 'min' };
    }
    const hours = totalSeconds / 3600;
    return { value: hours.toFixed(2), unit: 'hr' };
};

export default function ClientPage() {
  const [tripState, setTripState] = useState({
      speed: 0,
      distance: 0,
      duration: 0,
      maxSpeed: 0,
      totalStopTime: 0,
      stopCount: 0,
      timeTo100: null as number | null,
      oneKmResult: null as { time: number; maxSpeed: number } | null,
  });

  const [isTripActive, setTripActive] = useState(false);
  const [tripHistory, setTripHistory] = useState<Trip[]>([]);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [showVscIndicator, setShowVscIndicator] = useState(false);
  const [lightStatus, setLightStatus] = useState<'idle' | 'sequence' | 'go' | 'stopped' | 'stopping'>('idle');
  const [lightCount, setLightCount] = useState(0);

  const [isSpeedCheckActive, setIsSpeedCheckActive] = useState(false);
  const [speedCheckStartPoint, setSpeedCheckStartPoint] = useState<TripDataPoint | null>(null);
  const [liveAvgSpeed, setLiveAvgSpeed] = useState<number | null>(null);

  const tripMetricsRef = useRef({
    distance: 0,
    duration: 0,
    maxSpeed: 0,
    tripData: [] as TripDataPoint[],
    timeTo100: null as number | null,
    oneKmResult: null as { time: number; maxSpeed: number } | null,
    totalStopTime: 0,
    stopCount: 0,
    speed: 0,
    lastTick: 0,
    wasStopped: true,
    hasTripStartedMoving: false,
    lastLocation: null as { lat: number; lon: number } | null,
    completedSpeedChecks: [] as SpeedCheckResult[],
    significantEvents: [] as TripDataPoint[],
  });

  const locationRef = useRef<{ lat: number | null, lon: number | null }>({ lat: null, lon: null });
  const vscTimerRef = useRef<NodeJS.Timeout | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownCounterRef = useRef(0);
  
  const { toast } = useToast();

  const avgSpeed = useMemo(() => (tripState.duration > 0 ? tripState.distance / (tripState.duration / 3600) : 0), [tripState.distance, tripState.duration]);
  
  useEffect(() => {
    const savedHistory = localStorage.getItem('velocity-onsights-history');
    if (savedHistory) {
      try {
        setTripHistory(JSON.parse(savedHistory));
      } catch {
        // Ignore parsing errors
      }
    }
  }, []);

  const saveHistory = useCallback((newTrip: Trip) => {
    setTripHistory(prev => {
        const updatedHistory = [newTrip, ...prev];
        try {
            localStorage.setItem('velocity-onsights-history', JSON.stringify(updatedHistory));
        } catch (error: any) {
            console.error("Failed to save history to localStorage:", error);
            let description = 'Could not save the trip. The browser storage might be full.';
            if (error.name === 'QuotaExceededError') {
              description = 'Storage is full. Please clear some old trips from history to save new ones.';
            }
            toast({
                variant: 'destructive',
                title: 'Save Error',
                description: description,
            });
        }
        return updatedHistory;
    })
  }, [toast]);
  
  const resetSpeedCheck = useCallback(() => {
      setIsSpeedCheckActive(false);
      setSpeedCheckStartPoint(null);
      setLiveAvgSpeed(null);
  }, []);

  const resetTripMetrics = useCallback(() => {
    setTripState({
        speed: 0,
        distance: 0,
        duration: 0,
        maxSpeed: 0,
        totalStopTime: 0,
        stopCount: 0,
        timeTo100: null,
        oneKmResult: null,
    });
    setGeoError(null);
    setLightCount(0);
    resetSpeedCheck();
    
    locationRef.current = { lat: null, lon: null };

    tripMetricsRef.current = {
        speed: 0,
        distance: 0,
        duration: 0,
        maxSpeed: 0,
        tripData: [],
        timeTo100: null,
        oneKmResult: null,
        totalStopTime: 0,
        stopCount: 0,
        lastTick: 0,
        wasStopped: true,
        hasTripStartedMoving: false,
        lastLocation: null,
        completedSpeedChecks: [],
        significantEvents: [],
    };
  }, [resetSpeedCheck]);

  const stopTrip = useCallback(() => {
    setTripActive(false);
    setLightStatus('idle');
    setLightCount(0);
    playStopSound();

    setTimeout(() => {
        play100kmhSound();
    }, 500);

    const { 
        duration, distance, maxSpeed, stopCount, totalStopTime, 
        timeTo100, oneKmResult, completedSpeedChecks, significantEvents
    } = tripMetricsRef.current;
    
    const { lat, lon } = locationRef.current;
    const finalEvent: TripDataPoint = {
        timestamp: Date.now(),
        speed: tripMetricsRef.current.speed,
        lat, lon,
        distance: distance
    };
    
    const finalEvents = [...significantEvents, finalEvent];

    const currentAvgSpeed = duration > 0 ? distance / (duration / 3600) : 0;
    
    if (duration > 2) {
      const newTrip: Trip = {
        id: Date.now().toString(),
        date: new Date().toISOString(),
        duration,
        distance,
        avgSpeed: currentAvgSpeed,
        maxSpeed,
        stopCount,
        totalStopTime,
        timeTo100,
        oneKmResult,
        speedCheckResults: completedSpeedChecks,
        significantEvents: finalEvents,
        // tripData is intentionally omitted to save storage space
      };
      saveHistory(newTrip);
      toast({
        title: `Trip Saved`,
        description: `Your trip has been saved to your history.`,
      });
    }

    resetTripMetrics();
  }, [saveHistory, toast, resetTripMetrics]);

  const handleStartStopTrip = useCallback(() => {
    if (isTripActive) {
      stopTrip();
      return;
    }

    resetTripMetrics();
    playIntroTheme();

    const isStationary = tripMetricsRef.current.speed < 1;
    if (isStationary) {
        tripMetricsRef.current.stopCount = 1;
        setTimeout(() => {
            setLightStatus('sequence');
        }, 3000); 
    } else {
        setTripActive(true);
    }
}, [isTripActive, stopTrip, resetTripMetrics]);

  useEffect(() => {
      if (isTripActive && !tripMetricsRef.current.hasTripStartedMoving && locationRef.current.lat && locationRef.current.lon) {
          tripMetricsRef.current.lastLocation = { lat: locationRef.current.lat, lon: locationRef.current.lon };
          const startingPoint: TripDataPoint = {
              timestamp: Date.now(),
              speed: tripMetricsRef.current.speed,
              lat: locationRef.current.lat,
              lon: locationRef.current.lon,
              distance: 0
          };
          tripMetricsRef.current.tripData.push(startingPoint);
          tripMetricsRef.current.significantEvents.push(startingPoint);
      }
  }, [isTripActive]);
  
  const startGpsTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
     if (!navigator.geolocation) {
      const errorMsg = 'Geolocation is not supported by your browser.';
      setGeoError(errorMsg);
      return;
    }

    const handleSuccess = (position: GeolocationPosition) => {
      const speedInKmh = (position.coords.speed !== null) ? position.coords.speed * 3.6 : 0;
      tripMetricsRef.current.speed = speedInKmh;
      locationRef.current = { lat: position.coords.latitude, lon: position.coords.longitude };
      setGeoError(null);
    };

    const handleError = (error: GeolocationPositionError) => {
      let message = 'An unknown geolocation error occurred.';
      switch (error.code) {
        case error.PERMISSION_DENIED:
          message = 'Please enable location permissions to track your trip.';
          if (isTripActive) stopTrip();
          break;
        case error.POSITION_UNAVAILABLE:
          message = 'Location information is currently unavailable. The trip will continue.';
          break;
        case error.TIMEOUT:
          message = 'The request to get your location timed out. The trip will continue.';
          break;
      }
      setGeoError(message);
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 10000,
      }
    );
  }, [isTripActive, stopTrip]);
  
  useEffect(() => {
    startGpsTracking();

    return () => {
        if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }
    };
  }, [startGpsTracking]);
  
  useEffect(() => {
    if (geoError) {
      toast({
        variant: 'destructive',
        title: 'Location Error',
        description: geoError,
      });
    }
  }, [geoError, toast]);
  
  useEffect(() => {
    if (lightStatus !== 'sequence') {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    countdownCounterRef.current = 0;
    setLightCount(0);

    countdownIntervalRef.current = setInterval(() => {
      if (!isTripActive) {
        countdownCounterRef.current += 1;
        const count = countdownCounterRef.current;
        
        if (count <= 5) {
          setLightCount(count);
          if (count <= 4) {
            playLaserBeep();
          } else {
            playCountdownBeep();
          }
        } else if (count === 6) {
          setLightStatus('go');
          playGoBeep();
          setTripActive(true);
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          setTimeout(() => {
              setLightStatus('idle');
          }, 3000);
        }
      } else {
         if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
      }
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [lightStatus, isTripActive]);


  useEffect(() => {
    if (!isTripActive) return;

    tripMetricsRef.current.lastTick = Date.now();

    const dataTimer = setInterval(() => {
      const now = Date.now();
      const timeDelta = (now - tripMetricsRef.current.lastTick) / 1000;
      tripMetricsRef.current.lastTick = now;
      
      if (timeDelta <= 0 || timeDelta > 2) return;

      const { current } = tripMetricsRef;
      current.duration += timeDelta;
      
      const { lat, lon } = locationRef.current;
      
      if (!current.hasTripStartedMoving) {
        if (current.speed >= 1) {
            current.hasTripStartedMoving = true;
        }
      } else {
        if (lat !== null && lon !== null && current.lastLocation && current.lastLocation.lat && current.lastLocation.lon && (current.lastLocation.lat !== lat || current.lastLocation.lon !== lon)) {
            const distanceThisTick = haversineDistance(current.lastLocation, { lat, lon });
            current.distance += distanceThisTick;
        }
      }
      
      current.lastLocation = (lat !== null && lon !== null) ? { lat, lon } : null;
      current.maxSpeed = Math.max(current.maxSpeed, current.speed);
      
      const isCurrentlyStopped = current.speed < 1;
      
      if (current.hasTripStartedMoving) {
        if (isCurrentlyStopped) {
            current.totalStopTime += timeDelta;
            if (!current.wasStopped) { // Just stopped
              current.stopCount += 1;
              const stopEvent: TripDataPoint = { timestamp: now, speed: current.speed, lat, lon, distance: current.distance };
              current.significantEvents.push(stopEvent);
            }
        } else if (current.wasStopped) { // Just started moving
            const startEvent: TripDataPoint = { timestamp: now, speed: current.speed, lat, lon, distance: current.distance };
            current.significantEvents.push(startEvent);
        }
      }
      current.wasStopped = isCurrentlyStopped;

      if (current.timeTo100 === null && current.speed >= 100) {
        current.timeTo100 = current.duration;
        play100kmhSound();
      }
      if (current.oneKmResult === null && current.distance >= 1) {
        current.oneKmResult = { time: current.duration, maxSpeed: current.maxSpeed };
      }
      
      const currentPoint: TripDataPoint = { timestamp: now, speed: current.speed, lat, lon, distance: current.distance };
      
      const lastDataPoint = current.tripData.length > 0 ? current.tripData[current.tripData.length - 1] : null;
      const timeSinceLastSave = lastDataPoint ? (now - lastDataPoint.timestamp) / 1000 : Infinity;
      
      let shouldSaveDataPoint = false;
      if (!lastDataPoint) {
        shouldSaveDataPoint = true; 
      } else {
        const tripDurationMinutes = current.duration / 60;
        if (tripDurationMinutes < 30) { 
          if (timeSinceLastSave >= 10) shouldSaveDataPoint = true;
        } else if (tripDurationMinutes < 120) { 
          if (timeSinceLastSave >= 60) shouldSaveDataPoint = true;
        } else { 
          if (timeSinceLastSave >= 120) shouldSaveDataPoint = true;
        }
      }
      
      if (shouldSaveDataPoint) {
        current.tripData.push(currentPoint);
      }

      if (isSpeedCheckActive && speedCheckStartPoint) {
        const segmentTime = (currentPoint.timestamp - speedCheckStartPoint.timestamp) / 1000;
        const segmentDistance = currentPoint.distance - speedCheckStartPoint.distance;
        if (segmentTime > 0) {
          const avg = segmentDistance / (segmentTime / 3600);
          setLiveAvgSpeed(avg);
        }
      }

    }, 1000);

    const uiUpdateTimer = setInterval(() => {
        const { current } = tripMetricsRef;
        setTripState({
            speed: current.speed,
            distance: current.distance,
            duration: current.duration,
            maxSpeed: current.maxSpeed,
            totalStopTime: current.totalStopTime,
            stopCount: current.stopCount,
            timeTo100: current.timeTo100,
            oneKmResult: current.oneKmResult,
        });
    }, 250);

    return () => {
      clearInterval(dataTimer);
      clearInterval(uiUpdateTimer);
    };
  }, [isTripActive, isSpeedCheckActive, speedCheckStartPoint]);

  useEffect(() => {
    const requestWakeLock = async () => {
      if ('wakeLock' in navigator && isTripActive) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        } catch (err) {
        }
      }
    };

    const releaseWakeLock = () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };

    if (isTripActive) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isTripActive) {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      releaseWakeLock();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isTripActive]);
  
  useEffect(() => {
    if (vscTimerRef.current) {
      clearTimeout(vscTimerRef.current);
      vscTimerRef.current = null;
    }
    setShowVscIndicator(false);
    
    if (isTripActive && tripMetricsRef.current.wasStopped) {
      vscTimerRef.current = setTimeout(() => {
        if (isTripActive && tripMetricsRef.current.wasStopped) {
          playStationarySound();
          setShowVscIndicator(true);
        }
      }, 5 * 1000); 
    }
    
    return () => {
      if (vscTimerRef.current) {
        clearTimeout(vscTimerRef.current);
      }
    };
  }, [isTripActive, tripState.speed]); 

  const clearHistory = () => {
    setTripHistory([]);
    localStorage.removeItem('velocity-onsights-history');
    toast({ title: 'History Cleared', description: 'All trips have been deleted.' });
  };
  
  const deleteTrip = (tripId: string) => {
    setTripHistory(prev => {
        const updatedHistory = prev.filter(trip => trip.id !== tripId);
        localStorage.setItem('velocity-onsights-history', JSON.stringify(updatedHistory));
        toast({ title: 'Trip Deleted', description: 'The selected trip has been removed.' });
        return updatedHistory;
    });
  };
  
  const handleMarkStart = () => {
    const { speed, distance } = tripMetricsRef.current;
    const { lat, lon } = locationRef.current;
    
    const startPoint: TripDataPoint = {
        timestamp: Date.now(),
        speed,
        lat,
        lon,
        distance,
    };
    
    playMarkerStartSound();
    setSpeedCheckStartPoint(startPoint);
    setIsSpeedCheckActive(true);
  };
  
  const handleMarkEnd = () => {
    if (!speedCheckStartPoint) return;
    
    playMarkerEndSound();
    
    const { completedSpeedChecks } = tripMetricsRef.current;
    const { speed, distance } = tripMetricsRef.current;
    const { lat, lon } = locationRef.current;

    const endPoint: TripDataPoint = {
      timestamp: Date.now(),
      speed,
      lat,
      lon,
      distance,
    };
    
    const segmentDuration = (endPoint.timestamp - speedCheckStartPoint.timestamp) / 1000;
    if (segmentDuration <= 0) {
        resetSpeedCheck();
        return;
    }
    
    const segmentDistance = endPoint.distance - speedCheckStartPoint.distance;

    if (segmentDistance >= 0) {
        const newSpeedCheck: SpeedCheckResult = {
            id: `sc-${endPoint.timestamp}`,
            duration: segmentDuration,
            distance: segmentDistance,
            startPoint: speedCheckStartPoint,
            endPoint: endPoint,
        };
        
        tripMetricsRef.current.completedSpeedChecks = [...completedSpeedChecks, newSpeedCheck];
    }

    resetSpeedCheck();
  };
  
  const handleResetSpeedCheck = () => {
    resetSpeedCheck();
  };
  
  const LiveStat = ({ title, value, unit, className }: { title: string; value: string; unit: string; className?: string }) => (
    <div className="text-center">
      <p className="text-sm text-muted-foreground">{title}</p>
      <p className={cn("text-2xl font-bold tabular-nums", className || 'text-primary')}>{value}</p>
      <p className="text-xs text-muted-foreground -mt-1">{unit}</p>
    </div>
  );

  const durationDisplay = formatDuration(tripState.duration);
  const totalStopTimeDisplay = formatDuration(tripState.totalStopTime);
  const timeTo100Display = tripState.timeTo100 ? formatDuration(tripState.timeTo100) : null;
  const oneKmResultTimeDisplay = tripState.oneKmResult ? formatDuration(tripState.oneKmResult.time) : null;
  const distanceDisplayValue = tripState.distance >= 1 ? tripState.distance.toFixed(1) : tripState.distance.toFixed(2);

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground p-4 md:p-6 lg:p-8">
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Logo className="w-8 h-8 text-primary" />
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Velocity Onsights</h1>
        </div>
        <TripHistory trips={tripHistory} onClearHistory={clearHistory} onDeleteTrip={deleteTrip} />
      </header>
      
      <main className="flex-grow">
        <Card className="flex flex-col justify-between shadow-lg h-full">
          <CardHeader>
          </CardHeader>
           {geoError && (
              <div className="px-6 pb-4">
                  <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Location Error</AlertTitle>
                      <AlertDescription>
                      {geoError}
                      </AlertDescription>
                  </Alert>
              </div>
          )}
          <CardContent className="flex flex-col md:flex-row items-center justify-around gap-8 flex-grow">
            <div className="w-full flex-1 flex flex-col items-center justify-center">
              {isTripActive ? (
                <>
                  <Speedometer 
                    speed={tripState.speed}
                    avgSpeed={avgSpeed}
                    tripMaxSpeed={tripState.maxSpeed}
                    speedCheckStartSpeed={isSpeedCheckActive ? speedCheckStartPoint?.speed : undefined}
                    liveAvgSpeedCheck={isSpeedCheckActive ? liveAvgSpeed : undefined}
                  />
                  <div className="mt-6 flex items-center justify-center w-full max-w-sm gap-4">
                     <Button 
                        onClick={handleStartStopTrip}
                        variant="destructive"
                        size="lg"
                        className="h-20 w-20 rounded-full"
                      >
                        <Square className="h-8 w-8" />
                      </Button>
                    <div className="text-center flex-grow">
                      <span className="font-mono text-6xl font-bold text-primary tabular-nums">
                        {Math.round(tripState.speed)}
                      </span>
                      <span className="text-sm font-medium text-muted-foreground -mt-1 block">km/h</span>
                    </div>
                     <div className="w-20 h-20 flex items-center justify-center">
                       {isSpeedCheckActive ? (
                         <div className="flex flex-col items-center gap-2">
                            <Button onClick={handleMarkEnd} size="lg" className="w-full h-12">
                                <Flag className="mr-1 h-4 w-4" />
                                End
                            </Button>
                            <Button onClick={handleResetSpeedCheck} variant="ghost" size="icon" className="w-full h-6">
                                <RefreshCw className="h-3 w-3" />
                            </Button>
                         </div>
                       ) : !showVscIndicator ? (
                         <Button onClick={handleMarkStart} className="w-20 h-20 rounded-full bg-yellow-500/80 hover:bg-yellow-500/90 text-yellow-50" variant="secondary">
                           <Milestone className="h-8 w-8" />
                         </Button>
                       ) : null}
                     </div>
                  </div>
                  
                  {isSpeedCheckActive && (
                    <div className="w-full mt-4 p-2 rounded-lg border bg-card grid grid-cols-2 gap-4">
                        <LiveStat title="Start Speed" value={speedCheckStartPoint?.speed.toFixed(0) ?? '0'} unit="km/h" className="text-green-500" />
                        <LiveStat title="Live Avg. Speed" value={liveAvgSpeed?.toFixed(1) ?? '0.0'} unit="km/h" className="text-violet-400" />
                    </div>
                  )}

                  {showVscIndicator && !isSpeedCheckActive && (
                      <div className="mt-4 relative h-16 w-full max-w-sm rounded-lg bg-yellow-400/20 border-2 border-yellow-400 shadow-lg shadow-yellow-400/50 flex flex-col items-center justify-center text-yellow-400">
                          <ParkingCircle className="h-8 w-8" />
                          <span className="text-xs font-bold mt-1">STATIONARY</span>
                      </div>
                  )}
                </>
              ) : lightStatus !== 'idle' ? (
                <div className="h-[250px] flex items-center justify-center">
                    <TrafficLights status={lightStatus} lightCount={lightCount} />
                </div>
              ) : (
                <div className="h-[250px] flex items-center justify-center">
                  <Button 
                      onClick={handleStartStopTrip}
                      disabled={lightStatus !== 'idle'}
                      variant="destructive"
                      size="icon"
                      className="relative text-lg font-bold uppercase tracking-wider h-28 w-28 rounded-full shadow-lg shadow-destructive/50 transform hover:scale-105 transition-transform duration-200 flex items-center justify-center flex-col gap-1 disabled:scale-100"
                  >
                       {lightStatus !== 'idle' ? (
                          <Loader2 className="h-8 w-8 animate-spin" />
                       ) : (
                          <>
                              <Power className="h-8 w-8" />
                              <span>Start</span>
                          </>
                       )}
                  </Button>
                </div>
              )}
            </div>
            <div className="w-full md:w-auto grid grid-cols-2 gap-x-8 gap-y-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-primary/10 text-primary"><Timer className="w-6 h-6" /></div>
                <div><p className="text-sm text-muted-foreground">Duration</p><p className="text-2xl font-bold">{durationDisplay.value}</p><p className="text-sm text-muted-foreground -mt-1">{durationDisplay.unit}</p></div>
              </div>
               <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-primary/10 text-primary"><Waypoints className="w-6 h-6" /></div>
                <div><p className="text-sm text-muted-foreground">Distance</p><p className="text-2xl font-bold">{distanceDisplayValue}</p><p className="text-sm text-muted-foreground -mt-1">km</p></div>
              </div>
              <div className="flex items-center gap-4 text-yellow-400">
                <div className="p-3 rounded-full bg-yellow-400/10"><Gauge className="w-6 h-6" /></div>
                <div><p className="text-sm text-muted-foreground">Avg Speed</p><p className="text-2xl font-bold">{avgSpeed.toFixed(0)}</p><p className="text-sm text-yellow-400/80 -mt-1">km/h</p></div>
              </div>
              <div className="flex items-center gap-4 text-red-500">
                <div className="p-3 rounded-full bg-red-500/10"><Gauge className="w-6 h-6" /></div>
                <div><p className="text-sm text-muted-foreground">Max Speed</p><p className="text-2xl font-bold">{tripState.maxSpeed.toFixed(0)}</p><p className="text-sm text-red-500/80 -mt-1">km/h</p></div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-primary/10 text-primary"><ParkingCircle className="w-6 h-6" /></div>
                <div><p className="text-sm text-muted-foreground">Stops</p><p className="text-2xl font-bold">{tripState.stopCount.toString()}</p></div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-primary/10 text-primary"><TimerOff className="w-6 h-6" /></div>
                <div><p className="text-sm text-muted-foreground">Time Stopped</p><p className="text-2xl font-bold">{totalStopTimeDisplay.value}</p><p className="text-sm text-muted-foreground -mt-1">{totalStopTimeDisplay.unit}</p></div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-primary/10 text-primary"><Zap className="w-6 h-6" /></div>
                <div><p className="text-sm text-muted-foreground">0-100 km/h</p><p className="text-2xl font-bold">{timeTo100Display ? timeTo100Display.value : '--'}</p>{timeTo100Display?.unit && <p className="text-sm text-muted-foreground -mt-1">{timeTo100Display.unit}</p>}</div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-primary/10 text-primary"><Flag className="w-6 h-6" /></div>
                <div><p className="text-sm text-muted-foreground">1km Time</p><p className="text-2xl font-bold">{oneKmResultTimeDisplay ? oneKmResultTimeDisplay.value : '--'}</p>{oneKmResultTimeDisplay?.unit && <p className="text-sm text-muted-foreground -mt-1">{oneKmResultTimeDisplay.unit}</p>}{tripState.oneKmResult && <p className="text-xs text-muted-foreground mt-1">{`Max: ${tripState.oneKmResult.maxSpeed.toFixed(0)} km/h`}</p>}</div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex-col sm:flex-row gap-4 items-center">
            <p className="text-xs text-muted-foreground text-center sm:text-left flex-1">
              For accurate tracking, please keep this app open and on-screen during your trip.
            </p>
          </CardFooter>
        </Card>
      </main>
    </div>
  );
}

    

    

    

    
